import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

import * as analytics from '@adsk/acs-web-analytics';
import A from '@adsk/alloy-react-a';
import { Box, Flex, Text } from '@adsk/alloy-react-box';
import { displayDate, VALID_DATE_FORMATS } from '@adsk/alloy-react-datetime';
import Divider from '@adsk/alloy-react-divider';
import { isContentOverflowingElement } from '@adsk/alloy-react-helpers';
import { useLocale } from '@adsk/alloy-react-locale';
import ProgressBar from '@adsk/alloy-react-progress-bar';
import theme from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import BlockRemoveButton from '../../../../../BlockRemoveButton';
import { formatPath } from '../../../helpers';
import {
  scheduleRelationshipsMetadata,
  SCHEDULE_ANALYTICS,
} from '../../helpers/consts';
import { useScheduleActivityLink } from '../../hooks';
import { ActivityType, ScheduleActivityEntityBlockProps } from '../../types';

function ScheduleActivityEntityBlock({
  projectId,
  entity: { activity },
  onRemoveClick,
  onReferenceClickAnalytics,
  sourceEntityType,
}: ScheduleActivityEntityBlockProps) {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));
  const scheduleActivityLink = useScheduleActivityLink(activity);

  const {
    sequentialId,
    name,
    isCriticalPath,
    actualStart,
    start,
    plannedStart,
    actualFinish,
    finish,
    plannedFinish,
    completionPercentage: _completionPercentage,
    wbsPathActivities,
    type,
    durationDays,
    scheduleName,
    permittedActions,
  } = activity;
  const completionPercentage = Number(
    ((_completionPercentage ?? 0) * 100).toFixed(0),
  );
  const canRemoveReference = permittedActions?.includes('edit');

  const startDateToDisplay = actualStart ?? start ?? plannedStart;
  const finishDateToDisplay = actualFinish ?? finish ?? plannedFinish;

  const startDate = startDateToDisplay ? new Date(startDateToDisplay) : '';
  const finishDate = finishDateToDisplay ? new Date(finishDateToDisplay) : '';

  const ref = useRef<HTMLDivElement>(null);

  // Original order is parent > grandparent > great grandparent > ... > top ancestor
  const path =
    wbsPathActivities?.map(({ name }) => name ?? 'Untitled Activity') ?? [];
  const formattedPath = formatPath(path);

  const removeButtonClassName = 'ScheduleReferencePickerPlugin__remove';

  const [overflowed, setOverflowed] = useState<boolean>(false);
  const onClick = useCallback(() => {
    analytics.track(SCHEDULE_ANALYTICS.VIEW, {
      reference_type: sourceEntityType,
      // eslint-disable-next-line spellcheck/spell-checker
      referancing_tool: scheduleRelationshipsMetadata.type,
      project_id: projectId,
    });
    onReferenceClickAnalytics?.();
  }, [projectId, onReferenceClickAnalytics, sourceEntityType]);

  useLayoutEffect(() => {
    const isOverflowed = isContentOverflowingElement(ref);
    setOverflowed(isOverflowed);
  }, []);

  return (
    <A
      href={scheduleActivityLink}
      onClick={onClick}
      target="_blank"
      style={{
        cursor: 'pointer',
        [`&:hover .${removeButtonClassName}`]: {
          visibility: 'visible',
        },
      }}
    >
      <Flex
        flexDirection="column"
        position="relative"
        overflow="hidden"
        borderBottom={`1px solid ${theme.colors.charcoal100}`}
        color="charcoal900"
        padding="14px 16px 12px 16px"
      >
        <Flex alignItems="center">
          {type === ActivityType.Milestone && (
            <Box
              backgroundColor={isCriticalPath ? 'red300' : 'charcoal300'}
              borderRadius="2px"
              width="11px"
              height="11px"
              marginRight="8px"
              style={{ transform: 'rotate(45deg)' }}
            />
          )}
          <Flex
            typography="bodyMediumBold"
            color="charcoal900"
            marginRight="8px"
            maxWidth="300px"
          >
            {`#${sequentialId} - ${
              name ?? T.translate('NO_SCHEDULE_ACTIVITY_NAME')
            }`}
          </Flex>
          <Box typography="bodySmall" color="red700">
            {isCriticalPath ? T.translate('CRITICAL') : ''}
          </Box>

          {canRemoveReference && (
            <Flex alignItems="center" marginLeft="auto">
              <BlockRemoveButton
                className={removeButtonClassName}
                style={{
                  visibility: 'hidden',
                }}
                onClick={() => onRemoveClick()}
              />
            </Flex>
          )}
        </Flex>

        <Box
          ref={ref}
          display="grid"
          alignItems="center"
          typography="bodySmall"
          color="charcoal900"
          gridAutoFlow="column"
          width="100%"
          marginTop="6px"
          {...(overflowed && {
            gridTemplateColumns: 'repeat(3,max-content)',
            gridTemplateRows: 'max-content max-content',
          })}
          {...(!overflowed && {
            gridAutoColumns: 'max-content',
          })}
          style={{ columnGap: '12px', ...(overflowed && { rowGap: '4px' }) }}
        >
          <Flex alignItems="center" gridColumn={overflowed ? '1 / -1' : 'auto'}>
            <ProgressBar style={{ width: 56 }} now={completionPercentage} />
            <Text marginLeft="8px">{completionPercentage}%</Text>
          </Flex>
          {!overflowed && (
            <Divider
              variant="vertical"
              color={theme.colors.charcoal400}
              height={12}
            />
          )}
          <Box justifySelf="center" gridColumn={overflowed ? 3 : 'auto'}>
            {startDate &&
              displayDate(startDate, {
                format: VALID_DATE_FORMATS.NUMERIC,
              })}{' '}
            -{' '}
            {finishDate &&
              displayDate(finishDate, { format: VALID_DATE_FORMATS.NUMERIC })}
          </Box>
          <Divider
            variant="vertical"
            color={theme.colors.charcoal400}
            height={12}
            style={{ gridColumn: overflowed ? '2' : 'auto' }}
          />
          <OverflowTooltip>
            {typeof durationDays === 'number'
              ? `${durationDays} ${T.translate('DAYS')}`
              : '-'}
          </OverflowTooltip>
        </Box>

        <Box typography="bodySmall" color="charcoal800" marginTop="8px">
          {scheduleName}
        </Box>
        <Box typography="caption" color="charcoal500">
          {path.length > 0 && `└ ${formattedPath}`}
        </Box>
      </Flex>
    </A>
  );
}

export default ScheduleActivityEntityBlock;
